import { useEffect, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/components/PriceBlock";

const GiftRow = ({ g, onUpdate }) => {
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try {
      onUpdate((await api.post(`/gifts/${g.id}/resend`)).data);
      toast.success("Gift email sent again");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const redeemed = g.status === "redeemed";
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-4" data-testid={`gift-row-${g.id}`}>
      <div className="min-w-0">
        <p className="text-sm text-slate-200 truncate">{g.recipient_name || g.recipient_email}</p>
        <p className="text-xs text-slate-500 truncate">{g.recipient_name && `${g.recipient_email} · `}{fmtDate(g.created_at)}</p>
        {g.email_error && !redeemed && <p className="text-xs text-rose-400 mt-1">The email didn't go out. Try resending.</p>}
      </div>
      <div className="flex items-center gap-3">
        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-code ${redeemed ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : "bg-amber-500/10 text-amber-300 border border-amber-500/30"}`} data-testid={`gift-status-${g.id}`}>
          {redeemed ? "Redeemed" : "Sent"}
        </span>
        {!redeemed && (
          <button onClick={resend} disabled={busy} className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-amber-300" data-testid={`gift-resend-${g.id}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Resend
          </button>
        )}
      </div>
    </li>
  );
};

export const GiftsSent = () => {
  const [gifts, setGifts] = useState(null);
  useEffect(() => { api.get("/gifts/sent").then((r) => setGifts(r.data)); }, []);
  const update = (g) => setGifts((list) => list.map((x) => (x.id === g.id ? g : x)));
  return (
    <section className="rounded-2xl border border-line bg-panel p-6" data-testid="gifts-sent-list">
      <h2 className="font-display text-2xl text-amber-100 mb-2">Gifts you've sent</h2>
      {gifts === null ? <p className="text-sm text-slate-500">Loading...</p>
        : gifts.length === 0 ? <p className="text-sm text-slate-500" data-testid="gifts-empty">No gifts yet. The first one is always the best.</p>
        : <ul className="divide-y divide-line">{gifts.map((g) => <GiftRow key={g.id} g={g} onUpdate={update} />)}</ul>}
    </section>
  );
};
